import React, { useEffect, useRef, useState, useMemo } from "react";
import { HttpMainApi } from "../../interface/main-api";

const mainApi = new HttpMainApi();

interface HologramButtonProps {
  text?: string;
  onClick?: () => void;
  width?: string;
  height?: string;
}

const HologramButtonPage: React.FC<HologramButtonProps> = ({
  text = "✨ Hologram ✨",
  onClick,
  width = "280px",
  height = "70px",
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [gamma, setGamma] = useState(0);
  const [beta, setBeta] = useState(0);

  // 자이로센서 이벤트 등록
  useEffect(() => {
    const handleOrientation = (event: DeviceOrientationEvent) => {
      setGamma(event.gamma ?? 0);
      setBeta(event.beta ?? 0);
    };

    if (
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof (DeviceOrientationEvent as any).requestPermission === "function"
    ) {
      (DeviceOrientationEvent as any)
        .requestPermission()
        .then((res: string) => {
          if (res === "granted") {
            window.addEventListener(
              "deviceorientation",
              handleOrientation,
              true
            );
          }
        });
    } else {
      window.addEventListener("deviceorientation", handleOrientation, true);
    }

    return () => {
      window.removeEventListener("deviceorientation", handleOrientation);
    };
  }, []);

  // 계산된 스타일 값 (최적화)
  const { bgPos, highlight } = useMemo(() => {
    const bg = `${50 + gamma}% ${50 + beta}%`;
    const angle = (gamma / 90) * 180;
    const hl = `linear-gradient(${angle}deg, rgba(255,255,255,0.7), transparent 70%)`;
    return { bgPos: bg, highlight: hl };
  }, [gamma, beta]);

  // API 호출 예시
  const handleClick = async () => {
    if (onClick) {
      onClick();
      return;
    }
    try {
      const param: any = {};
      const res = await mainApi.today_check(param);
      console.log("res : ", res);
      alert(res.code);
    } catch (err) {
      console.error("API 호출 실패:", err);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        background: "#0d0d0d",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        fontFamily: "sans-serif",
      }}
    >
      <button
        ref={buttonRef}
        onClick={handleClick}
        style={{
          position: "relative",
          width,
          height,
          fontSize: "1.2rem",
          fontWeight: "bold",
          border: "none",
          borderRadius: "40px",
          color: "#000",
          background: `
      linear-gradient(135deg, #fdfdfd, #dadada, #fef9ff, #e1eaff)
    `,
          backgroundSize: "200% 200%",
          backgroundPosition: bgPos,
          transition: "background-position 0.2s ease-out",
          boxShadow:
            "0 0 25px rgba(255,255,255,0.4), inset 0 0 15px rgba(255,255,255,0.2)",
          overflow: "hidden",
          zIndex: 1,
          cursor: "pointer",
        }}
      >
        {/* 무지개빛 컬러 소스 */}
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: `
        conic-gradient(from 180deg, #ff00cc, #00ffee, #ffee00, #ff00cc),
        linear-gradient(45deg, #ff9ff3, #feca57, #48dbfb, #1dd1a1)
      `,
            backgroundSize: "300% 300%",
            backgroundPosition: bgPos,
            mixBlendMode: "color-dodge",
            animation: "rainbowShift 8s linear infinite",
            opacity: 0.8,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        {/* 홀로그램 패턴 레이어 */}
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            backgroundImage: "url('/images/holo1.png')",
            backgroundSize: "cover",
            backgroundPosition: bgPos,
            mixBlendMode: "overlay",
            opacity: 0.7,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        {/* 반사 레이어 */}
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            backgroundImage: highlight,
            mixBlendMode: "screen",
            opacity: 0.9,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        {/* 글리터 반짝임 레이어 */}
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            backgroundImage: "url('/images/holo3.png')",
            backgroundSize: "200% 200%",
            backgroundPosition: "center",
            animation:
              "glitterShift 6s linear infinite, sparkleBlink 2s infinite ease-in-out",
            mixBlendMode: "screen",
            opacity: 0.6,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        <span style={{ position: "relative", zIndex: 1 }}>{text}</span>

        {/* keyframes */}
        <style>
          {`
      @keyframes rainbowShift {
        0% { background-position: 0% 0%; }
        50% { background-position: 100% 100%; }
        100% { background-position: 0% 0%; }
      }
      @keyframes glitterShift {
        0% { background-position: 0% 0%; }
        50% { background-position: 100% 100%; }
        100% { background-position: 0% 0%; }
      }
      @keyframes sparkleBlink {
        0%, 100% { opacity: 0.4; filter: brightness(1); }
        50% { opacity: 0.8; filter: brightness(1.6); }
      }
    `}
        </style>
      </button>

      {/* 디버깅용 센서 출력 */}
      <div style={{ marginTop: 32, fontSize: "0.9rem", color: "#ccc" }}>
        gamma: {gamma.toFixed(2)}° &nbsp;&nbsp; beta: {beta.toFixed(2)}°
      </div>

      {/* keyframes 정의 */}
      <style>
        {`
          @keyframes glitterShift {
            0% { background-position: 0% 0%; }
            50% { background-position: 100% 100%; }
            100% { background-position: 0% 0%; }
          }
        `}
      </style>
    </div>
  );
};

export default HologramButtonPage;
